import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { ImportIcon } from 'assets/images/icons';
import Button from '@components/Button';
import ImageCropModal from '@components/ImageCropModal';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { validateFileSize, validateFileParams } from '@helpers/upload-file';
import { MEDIA_TYPE } from '@constants/file';

const Container = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

const ImageWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8,
    marginRight: 8,
    width: 74,
    height: 74,
    background: 'var(--action-disabled-background)',
    borderRadius: 8,
});

const UploadImage = styled('img')({
    display: 'inline-block',
    width: 58,
    height: 58,
    borderRadius: 8,
    backgroundColor: '#fff',
    objectFit: 'contain',
});

/**
 * @param {Object} style for container
 * @param {number} maxSize max size of the picture in bytes
 * @param {number} minSize min size of the picture in bytes
 * @param {string} extensions allowed extensions
 * @param {Array} dimension [minWidth, minHeight, maxWidth, maxHeight]
 * @param {string} logoUrl url of the picture
 * @param {boolean} disabled disabled state
 * @param {function} onDelete function to clear picture
 * @param {function} uploadLogo function to upload the picture
 * @param {Object} cropConfig crop configuration { aspectRatio, outputWidth?, outputMinWidth?, outputMaxWidth? }
 */
const ImageUploaderSingle = ({
    style = {},
    maxSize,
    minSize,
    extensions,
    dimension,
    logoUrl,
    disabled,
    onDelete,
    uploadLogo,
    isUploading,
    cropConfig,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);

    const onUpload = async e => {
        const file = e.target.files[0];
        if (!file) return;

        if (cropConfig) {
            const objUrl = URL.createObjectURL(file);
            e.target.value = '';
            setCropImageSrc(objUrl);
            setCropModalOpen(true);
            return;
        }

        if (dimension) {
            const objUrl = URL.createObjectURL(file);
            await validateFileParams(MEDIA_TYPE.IMAGE, objUrl, { dimension }, () => {
                dispatch(alertActions.error(t('apiErrors.invalid_format')));
                e.target.value = '';
                URL.revokeObjectURL(objUrl);
                return;
            });
        }
        const { error, metric, size } = validateFileSize(file, maxSize, minSize);
        if (error) {
            dispatch(alertActions.error(t(`apiErrors.${error}`, { size, metric })));
            e.target.value = '';
            return;
        }
        uploadLogo(e);
    };

    const onCropSave = croppedFile => {
        setCropModalOpen(false);
        URL.revokeObjectURL(cropImageSrc);
        setCropImageSrc(null);
        uploadLogo({ target: { files: [croppedFile] } });
    };

    const onCropClose = () => {
        setCropModalOpen(false);
        URL.revokeObjectURL(cropImageSrc);
        setCropImageSrc(null);
    };

    return (
        <>
            <Container style={{ ...style }}>
                {logoUrl && (
                    <ImageWrapper>
                        <UploadImage src={logoUrl} alt="logo" />
                    </ImageWrapper>
                )}
                <Button
                    variant="outlined"
                    aria-label="upload picture"
                    component="label"
                    disabled={disabled}
                    startIcon={
                        !isUploading ? (
                            <ImportIcon />
                        ) : (
                            <CircularProgress sx={{ color: 'var(--text-tertiary)' }} size={16} />
                        )
                    }
                >
                    <input hidden accept={extensions ? extensions : 'image/*'} type="file" onChange={onUpload} />
                    {logoUrl ? t('buttons.replace') : t('buttons.upload')}
                </Button>
                {logoUrl ? (
                    <Button
                        variant="outlined"
                        sx={{ minWidth: 'auto', marginLeft: '8px' }}
                        disabled={disabled}
                        onClick={onDelete}
                    >
                        <DeleteIcon sx={{ color: 'var(--text-secondary)' }} />
                    </Button>
                ) : null}
            </Container>
            {cropConfig && (
                <ImageCropModal
                    open={cropModalOpen}
                    imageSrc={cropImageSrc}
                    aspectRatio={cropConfig.aspectRatio}
                    outputWidth={cropConfig.outputWidth}
                    outputMinWidth={cropConfig.outputMinWidth}
                    outputMaxWidth={cropConfig.outputMaxWidth}
                    maxSize={maxSize}
                    minSize={minSize}
                    onClose={onCropClose}
                    onSave={onCropSave}
                />
            )}
        </>
    );
};

export default ImageUploaderSingle;
